import React from 'react';
import type { DropzoneOptions, DropzoneRef } from 'react-dropzone';
import Dropzone from 'react-dropzone';
import filesize from 'filesize';
import { Line } from 'rc-progress'; // TODO: remove in NGTPA-15545

import FormHelperText from '~/components/core/Atomic/FormHelperText';
import type { GridItemsAlignment } from '~/components/core/Atomic/Grid/Grid';
import Grid from '~/components/core/Atomic/Grid/Grid';
import CancelButton from '~/components/core/Buttons/CancelButton';
import Text from '~/components/core/TextComponents/Text';
import { FileIcon, UploadIcon } from '~/components/icons';
import cn from '~/Utils/cn';

interface FileDropZoneProps {
  uploadPercent: number;
  error?: boolean;
  errorMessage?: string;
  file?: File;
  onFileSelect: (file: File) => void;
  onCancelFileSelect: () => void;
  dropZoneOptions?: DropzoneOptions;
  fileDisplayComponent?: React.ReactNode;
  baseStyleOverride?: React.CSSProperties;
  iconComponent?: React.ReactNode;
  gridContainerAlignItems?: GridItemsAlignment;
  disabled?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & { 'data-testid'?: string };
}

const baseStyle: React.CSSProperties = {
  width: 294,
  height: 180,
  borderWidth: '1px',
  borderColor: '#D0D0D0',
  borderStyle: 'dashed',
  borderRadius: '10px',
};

const activeStyle: React.CSSProperties = {
  borderStyle: 'solid',
  borderColor: '#6c6',
  backgroundColor: '#eee',
};

const FileDropZone: React.FC<FileDropZoneProps> = ({
  uploadPercent,
  error,
  errorMessage,
  file,
  onFileSelect,
  onCancelFileSelect,
  dropZoneOptions,
  fileDisplayComponent,
  baseStyleOverride = {},
  iconComponent,
  gridContainerAlignItems = 'center',
  disabled,
  inputProps = {},
}) => {
  const dropzoneRef = React.useRef<DropzoneRef>(null);
  const isFileChosen = !!file;

  // Important: react-dropzone doesn't manage dropped files. You need to destroy the object URL yourself whenever you don't need the preview by calling window.URL.revokeObjectURL(file.preview); to avoid memory leak
  return (
    <div className="flex w-full justify-center">
      <Grid
        container
        className="h-full w-full"
        justify="center"
        alignItems={gridContainerAlignItems as GridItemsAlignment}
        direction="column"
      >
        <Grid item xs={12} className="flex w-full justify-center">
          <Dropzone
            onDrop={(files) => {
              if (files[0]) {
                onFileSelect(files[0]);
              }
            }}
            multiple={false}
            ref={dropzoneRef}
            {...dropZoneOptions}
          >
            {({ getRootProps, getInputProps, isDragActive }) => {
              const isUploading = uploadPercent > 0 && uploadPercent < 100;
              return (
                <div
                  {...getRootProps({
                    // Disable click and keydown behavior
                    onClick: (event) => event.stopPropagation(),
                    onKeyDown: (event) => {
                      if (event.keyCode === 32 || event.keyCode === 13) {
                        event.stopPropagation();
                      }
                    },
                  })}
                  style={{
                    ...(isDragActive
                      ? { ...baseStyle, ...baseStyleOverride, ...activeStyle }
                      : { ...baseStyle, ...baseStyleOverride }),
                  }}
                >
                  <Grid container className="flex h-full flex-col items-center justify-center">
                    {isFileChosen && (
                      <>
                        <Grid item>
                          <Text
                            variant={Text.VARIANTS.SM}
                            weight={Text.WEIGHTS.REGULAR}
                            colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                          >
                            File chosen
                          </Text>
                        </Grid>
                        <Grid item className="flex items-center">
                          <FileIcon />
                          <span className="p-8">{file?.name}</span>
                          <span className="text-sm text-slate-800">{filesize(file?.size || 0)}</span>
                        </Grid>
                        {fileDisplayComponent ? <Grid item>{fileDisplayComponent}</Grid> : null}
                        <Grid item className={cn('mx-12 my-4 h-20')}>
                          <Line
                            className={cn('w-full stroke-teal-600', { hidden: !isUploading })}
                            percent={uploadPercent}
                            strokeWidth={1.5}
                            strokeColor="inherit"
                          />
                        </Grid>
                        <Grid item>
                          <CancelButton
                            onClick={onCancelFileSelect}
                            style={{ margin: '0' }}
                            content="remove"
                            disabled={disabled}
                          />
                        </Grid>
                      </>
                    )}
                    {!isFileChosen && (
                      <>
                        <Grid item className="text-slate-700">
                          {iconComponent ?? <UploadIcon size={16} iconColor="currentColor" />}
                        </Grid>
                        <Grid item className="pt-6 flex flex-col items-center pb-12">
                          <Text
                            variant={Text.VARIANTS.SM}
                            weight={Text.WEIGHTS.REGULAR}
                            colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                          >
                            Drag & Drop your file here
                          </Text>
                          <Text
                            variant={Text.VARIANTS.SM}
                            weight={Text.WEIGHTS.REGULAR}
                            colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                          >
                            or
                          </Text>
                        </Grid>
                        <Grid item>
                          <input {...getInputProps()} {...inputProps} />
                          <CancelButton
                            onClick={() => dropzoneRef.current?.open()}
                            content="BROWSE"
                            withMarginRight={false}
                            disabled={disabled}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </div>
              );
            }}
          </Dropzone>
        </Grid>
        <Grid item xs={12}>
          {error && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Grid>
      </Grid>
    </div>
  );
};

export default FileDropZone;
